/**
 * QuickActionsGrid — top-of-Dashboard 4-card grid for primary creation flows
 * (New Slideshow, New Song, Upload, AI Generate).
 *
 * Purely presentational; the list of actions is static metadata and the parent
 * owns the actual click handler (so subscription/limit checks stay co-located).
 */
import { motion } from "framer-motion";
import {
  Presentation, Music, Upload, Sparkles, Plus, BookOpen,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

export const QUICK_ACTIONS = [
  { id: "new-slideshow", titleKey: "new_slideshow",   descKey: "new_slideshow_desc",   icon: Presentation, color: "from-blue-500 to-blue-600",     action: "create" },
  { id: "new-song",      titleKey: "new_song",        descKey: "new_song_desc",        icon: Music,        color: "from-purple-500 to-purple-600", action: "song"   },
  { id: "from-verse",    titleKey: "from_verse",      descKey: "from_verse_desc_card", icon: BookOpen,     color: "from-amber-500 to-amber-700",   action: "from_verse" },
  { id: "upload-doc",    titleKey: "upload_document", descKey: "upload_document_desc", icon: Upload,       color: "from-green-500 to-green-600",   action: "upload" },
  { id: "ai-generate",   titleKey: "ai_generate",     descKey: "ai_generate_desc",     icon: Sparkles,     color: "from-amber-500 to-orange-500",  action: "ai"     },
];

export default function QuickActionsGrid({ t, onAction }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="mb-10"
    >
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Plus className="w-5 h-5" />
        {t("quick_actions") || "Quick Actions"}
      </h3>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="quick-actions-grid">
        {QUICK_ACTIONS.map((action, idx) => (
          <motion.div
            key={action.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 + idx * 0.05 }}
          >
            <Card
              className="cursor-pointer hover:shadow-lg transition-all hover:-translate-y-1 border-0 overflow-hidden group"
              onClick={() => onAction(action.action)}
              data-testid={`quick-action-${action.id}`}
            >
              <CardContent className="p-0">
                <div className={`bg-gradient-to-br ${action.color} p-6 text-white`}>
                  <action.icon className="w-8 h-8 mb-3 group-hover:scale-110 transition-transform" />
                  <h4 className="font-semibold text-lg">{t(action.titleKey)}</h4>
                  <p className="text-sm opacity-90">{t(action.descKey)}</p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </motion.section>
  );
}
